import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { copyText } from "@/lib/copyText";
import { MAX_COUNT, normalizeCount, countNoteText, type CountNote } from "./invisibleCount";

// U+2800 (Braille Pattern Blank) renders as a genuinely blank character with
// real width, which is why it works for "invisible" bios/messages -- unlike
// a true zero-width space, most platforms don't strip it out as empty input.
const VARIANTS = [
  { id: "braille", label: "Braille Blank", char: "\u2800", code: "U+2800", note: "Recommended. Has real width, so most apps accept it as non-empty." },
  { id: "hangul", label: "Hangul Filler", char: "\u3164", code: "U+3164", note: "Wide blank; often accepted for usernames and game names." },
  { id: "zwsp", label: "Zero-Width Space", char: "\u200b", code: "U+200B", note: "Truly invisible, but many apps strip it." },
  { id: "zwnj", label: "Zero-Width Non-Joiner", char: "\u200c", code: "U+200C", note: "Invisible; sometimes survives where U+200B is stripped." },
  { id: "zwj", label: "Zero-Width Joiner", char: "\u200d", code: "U+200D", note: "Invisible; can affect emoji next to it." },
  { id: "wordjoiner", label: "Word Joiner", char: "\u2060", code: "U+2060", note: "Zero-width and no-break. Some apps accept it as text; others treat it like the other zero-width characters and strip it." },
  { id: "invsep", label: "Invisible Separator", char: "\u2063", code: "U+2063", note: "Zero-width punctuation used between items. It is rarely on a platform's strip list, but it is not accepted everywhere." },
  { id: "emspace", label: "Em Space", char: "\u2003", code: "U+2003", note: "A wide blank that is ordinary whitespace, so forms that trim spaces will remove it." },
  { id: "linebreak", label: "Invisible Line Break", char: "\u2800\n", code: "U+2800 + newline", note: "A blank-looking line (use in bios and multi-line fields)." },
] as const;

const QUICK_LENGTHS = [1, 5, 10, 50] as const;

const TOOL_INFO_CONTENT = {
  what: `Generates invisible or blank-looking Unicode characters, commonly used to create a blank-looking username, bio, or message where normal empty text isn't allowed — a platform's "field can't be empty" validation is usually checking for zero characters, not checking whether those characters are visible, so a character that exists but renders as nothing satisfies the requirement.`,
  how: `Pick a character, set how many you want (type a number from 1 to 500, drag the slider, or press 1, 5, 10 or 50), copy it into the field that requires non-empty text, and check it in the Test box.`,
  tip: `Platforms change which of these they accept, so there is no list that stays true: paste the result into the Test box here to confirm what you copied, then try it in the real field. Not every platform accepts every invisible character the same way — if one doesn't work, try a different variant, since some platforms filter specific Unicode ranges that are commonly used for exactly this purpose.`,
};

export default function InvisibleTextGenerator() {
  const [count, setCount] = useState([1]);
  const [countText, setCountText] = useState("1");
  const [countNote, setCountNote] = useState<CountNote>("");
  const setCountBoth = (n: number) => { setCount([n]); setCountText(String(n)); setCountNote(""); };
  // The box always shows the number that will really be used (0 becomes 1, 501 becomes 500), with a note saying so.
  const onCountText = (v: string) => {
    const r = normalizeCount(v, count[0]);
    setCountText(r.text);
    setCount([r.count]);
    setCountNote(r.note);
  };
  const [variantId, setVariantId] = useState<string>("braille");
  const [test, setTest] = useState("");

  const variant = VARIANTS.find(v => v.id === variantId) ?? VARIANTS[0];
  const result = variant.char.repeat(count[0]);

  const { logUsageNow } = useLogUsageOnAction();

  const copy = async () => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    void copyText(result, "Copied! Paste it anywhere you'd use normal text.");
  };

  const codes = Array.from(test).map(c => "U+" + c.codePointAt(0)!.toString(16).toUpperCase().padStart(4, "0"));

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <div className="p-6 rounded-xl border bg-card min-h-[80px] flex items-center justify-center">
          <span className="text-xs text-muted-foreground" data-testid="inv-status">
            {count[0]} × {variant.label} ({variant.code}) ready to copy
          </span>
        </div>

        <div>
          <Label htmlFor="inv-count">Number of characters (1 to {MAX_COUNT})</Label>
          <Input
            id="inv-count"
            type="text"
            inputMode="numeric"
            value={countText}
            onChange={e => onCountText(e.target.value)}
            onBlur={() => { setCountText(String(count[0])); if (countNote === "empty") setCountNote(""); }}
            aria-invalid={countNote !== ""}
            className="mb-3 mt-1"
          />
          {countNote && <p role="alert" data-testid="inv-count-note" className="text-xs text-destructive mb-2">{countNoteText(countNote, count[0])}</p>}
          <div className="flex gap-2 mb-3" role="group" aria-label="Quick lengths">
            {QUICK_LENGTHS.map(n => (
              <Button key={n} size="sm" variant={count[0] === n ? "default" : "outline"} aria-pressed={count[0] === n} onClick={() => setCountBoth(n)}>{n}</Button>
            ))}
          </div>
          <Slider aria-label="Number of characters slider" value={[Math.min(count[0], 20)]} onValueChange={v => setCountBoth(v[0])} min={1} max={20} step={1} />
        </div>

        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Character type">
          {VARIANTS.map(v => (
            <Button key={v.id} size="sm" variant={variantId === v.id ? "default" : "outline"} onClick={() => setVariantId(v.id)} aria-pressed={variantId === v.id}>
              {v.label}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{variant.note}</p>

        <Button onClick={copy} className="w-full gap-2">
          <Copy className="h-4 w-4" /> Copy Invisible Text
        </Button>

        <div>
          <Label className="mb-1 block">Test box: paste it here to check it works</Label>
          <Textarea aria-label="Test box: paste it here to check it works" value={test} onChange={e => setTest(e.target.value)} placeholder="Paste here…" className="min-h-[60px]" />
          <p className="text-xs text-muted-foreground mt-1 font-mono break-all" data-testid="inv-codes">
            {codes.length ? `${codes.length} character${codes.length === 1 ? "" : "s"}: ${codes.slice(0, 40).join(" ")}${codes.length > 40 ? " …" : ""}` : "Nothing pasted yet"}
          </p>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Please use blank text responsibly. Don't use it to impersonate others, evade moderation or mislead people.
          Some platforms remove these characters.
        </p>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
